/**
 * Saved Colours — merge utilities.
 *
 * Pure functions (no I/O, no globals besides the export) that reconcile a
 * guest's localStorage state with the customer's remote state on login.
 *
 * Rules (see project brief):
 *  - Remote/customer data wins on conflicting colour fields (name/hex/handle/savedAt).
 *  - Unique local-only colours are preserved (never silently dropped).
 *  - Colours are de-duplicated by productId.
 *  - Groups are matched primarily by their stable id (so a rename on one
 *    side never merges into a duplicate row), falling back to
 *    case-insensitive name matching for groups created independently on
 *    each side; the default group is always a single, stable "default" id.
 *  - Local-only group *assignments* on a colour that also exists remotely are
 *    preserved by unioning groupIds, remapped into the merged group id space,
 *    so a customer never loses a custom-group assignment made while a guest.
 *  - Group deletions are tracked as tombstones (state.deletedGroupIds) and
 *    unioned across both sides, since a union merge otherwise has no way
 *    to tell "never existed on this side" apart from "existed and was
 *    deleted" — without this, a deleted group would just get merged back
 *    in from whichever side hadn't seen the delete yet.
 *  - Colour removals are tracked as timestamped tombstones
 *    (state.deletedColours: [{ productId, deletedAt }]), for the same
 *    reason — but timestamped rather than a plain id list, because a
 *    productId (unlike a group id) is legitimately reused: removed, then
 *    saved again. A colour only survives a merge if its own savedAt is
 *    at least as new as the latest tombstone for that productId, so a
 *    genuine re-save always outlives a stale removal.
 */
(function (global) {
  'use strict';

  var DEFAULT_GROUP_ID = 'default';

  function normalizedName(name) {
    return (name || '').trim().toLowerCase();
  }

  /**
   * Builds the merged group list plus a lookup from "source group id" (in
   * either local or remote state) to the id it maps to in the merged state.
   *
   * Groups are matched primarily by their stable `id` — NOT by name. A
   * group's id never changes once created (renameGroup() only edits its
   * `name`), so matching by id is what keeps a rename from producing a
   * second, duplicate group row when it merges against a not-yet-updated
   * copy of the same group under its old name. Name-matching is only a
   * fallback, for genuinely distinct groups created independently (e.g. a
   * guest's local group merging with an unrelated remote group that
   * happens to share a name).
   *
   * When the same id shows up on both sides with two different names (a
   * rename that raced with a merge from another device), whichever state
   * was updated more recently wins the name — otherwise the outcome would
   * depend only on processing order, and a stale name could silently
   * overwrite a newer rename made elsewhere.
   *
   * `deletedIds` is the union of both sides' delete tombstones (see
   * saved-colours-storage.js's deleteGroup()). A union merge has no other
   * way to represent "this group was removed" — without checking this
   * set, a group deleted on one device would just get added straight
   * back in by the very next merge against a copy that doesn't know it
   * was deleted yet.
   */
  function mergeGroups(localGroups, remoteGroups, localUpdatedAt, remoteUpdatedAt, deletedIds) {
    var mergedGroups = [];
    var byId = {};
    var byName = {};
    var localIdMap = {};
    var remoteIdMap = {};
    var localIsNewer = !!localUpdatedAt && (!remoteUpdatedAt || localUpdatedAt > remoteUpdatedAt);
    var deletedSet = deletedIds || {};

    (remoteGroups || []).forEach(function (group) {
      var id = group.isDefault ? DEFAULT_GROUP_ID : group.id;
      if (deletedSet[id]) return;
      var record = {
        id: id,
        name: group.isDefault ? 'All Saved Colours' : group.name,
        createdAt: group.createdAt,
        isDefault: !!group.isDefault,
      };
      byId[id] = record;
      byName[normalizedName(record.name)] = record;
      mergedGroups.push(record);
      remoteIdMap[group.id] = record.id;
    });

    (localGroups || []).forEach(function (group) {
      var id = group.isDefault ? DEFAULT_GROUP_ID : group.id;
      if (deletedSet[id]) return;

      var existingById = byId[id];
      if (existingById) {
        localIdMap[group.id] = existingById.id;
        if (localIsNewer && !group.isDefault && group.name !== existingById.name) {
          delete byName[normalizedName(existingById.name)];
          existingById.name = group.name;
          byName[normalizedName(existingById.name)] = existingById;
        }
        return;
      }

      var key = group.isDefault ? DEFAULT_GROUP_ID : normalizedName(group.name);
      var existingByName = byName[key];
      if (existingByName) {
        localIdMap[group.id] = existingByName.id;
        return;
      }

      var record = {
        id: id,
        name: group.isDefault ? 'All Saved Colours' : group.name,
        createdAt: group.createdAt,
        isDefault: !!group.isDefault,
      };
      byId[id] = record;
      byName[key] = record;
      mergedGroups.push(record);
      localIdMap[group.id] = record.id;
    });

    if (!byId[DEFAULT_GROUP_ID]) {
      var defaultGroup = { id: DEFAULT_GROUP_ID, name: 'All Saved Colours', createdAt: new Date().toISOString(), isDefault: true };
      mergedGroups.unshift(defaultGroup);
      byId[DEFAULT_GROUP_ID] = defaultGroup;
    }

    return { groups: mergedGroups, localIdMap: localIdMap, remoteIdMap: remoteIdMap };
  }

  // A colour is allowed to belong to zero groups deliberately (see
  // saved-colours-storage.js's removeFromGroup()) — the default-group
  // fallback below only applies when this colour's groups were actually
  // deleted out from under it (orphaned), never when it started at zero
  // groups already; otherwise every zero-groups colour would get swept
  // back into "Saved Colours" by any merge.
  function remapGroupIds(groupIds, idMap, deletedSet) {
    var hadGroups = !!(groupIds && groupIds.length);
    var mapped = (groupIds || [])
      .filter(function (id) { return !deletedSet || !deletedSet[id]; })
      .map(function (id) { return idMap[id] || id; });
    var unique = Array.from(new Set(mapped));
    if (unique.length) return unique;
    return hadGroups ? [DEFAULT_GROUP_ID] : [];
  }

  /**
   * Merges both sides' colour-removal tombstones into { productId:
   * latestDeletedAt }, keeping only the most recent deletedAt per
   * productId. Unlike group ids, a productId is legitimately reused —
   * removed, then saved again later — so this can't just be a set of
   * "permanently gone" ids; it has to carry a timestamp for
   * isSuppressedByTombstone() to compare against each colour's own
   * savedAt.
   */
  function mergeDeletedColours(localDeleted, remoteDeleted) {
    var map = {};
    (localDeleted || []).concat(remoteDeleted || []).forEach(function (entry) {
      if (!entry || entry.productId == null || !entry.deletedAt) return;
      var id = String(entry.productId);
      if (!map[id] || entry.deletedAt > map[id]) map[id] = entry.deletedAt;
    });
    return map;
  }

  // True when this productId was removed more recently than it was
  // (re)saved — i.e. the removal should win and this colour instance
  // should be dropped from the merge rather than resurrected.
  function isSuppressedByTombstone(productId, savedAt, deletedColoursMap) {
    var deletedAt = deletedColoursMap[String(productId)];
    if (!deletedAt) return false;
    return !savedAt || deletedAt >= savedAt;
  }

  /**
   * @param {object} localState  Output of SavedColoursStorage.getState()
   * @param {object} remoteState Customer's remote state (already parsed JSON), or null
   * @returns {object} merged state, ready to persist both locally and remotely
   */
  function mergeStates(localState, remoteState) {
    if (!remoteState) {
      return JSON.parse(JSON.stringify(localState));
    }

    var deletedGroupIds = Array.from(
      new Set((localState.deletedGroupIds || []).concat(remoteState.deletedGroupIds || []))
    );
    var deletedSet = {};
    deletedGroupIds.forEach(function (id) { deletedSet[id] = true; });

    var groupMerge = mergeGroups(localState.groups, remoteState.groups, localState.updatedAt, remoteState.updatedAt, deletedSet);
    var deletedColoursMap = mergeDeletedColours(localState.deletedColours, remoteState.deletedColours);
    var coloursByProductId = {};

    (remoteState.colours || []).forEach(function (colour) {
      if (!colour || colour.productId == null) return;
      var productId = String(colour.productId);
      if (isSuppressedByTombstone(productId, colour.savedAt, deletedColoursMap)) return;
      coloursByProductId[productId] = {
        productId: productId,
        handle: colour.handle,
        name: colour.name,
        hex: colour.hex,
        savedAt: colour.savedAt,
        groupIds: remapGroupIds(colour.groupIds, groupMerge.remoteIdMap, deletedSet),
      };
    });

    (localState.colours || []).forEach(function (colour) {
      if (!colour || colour.productId == null) return;
      var productId = String(colour.productId);
      // Skip local's contribution if it's stale relative to the
      // tombstone — but don't touch coloursByProductId here: if remote's
      // own (newer) copy of this productId already survived its own
      // check above, that entry must stand untouched, not get erased
      // just because this device's copy happens to be the older one.
      if (isSuppressedByTombstone(productId, colour.savedAt, deletedColoursMap)) {
        return;
      }
      var remapped = remapGroupIds(colour.groupIds, groupMerge.localIdMap, deletedSet);
      var existing = coloursByProductId[productId];

      if (existing) {
        // Remote wins on the descriptive fields; local group assignments are unioned in.
        existing.groupIds = Array.from(new Set(existing.groupIds.concat(remapped)));
      } else {
        coloursByProductId[productId] = {
          productId: productId,
          handle: colour.handle,
          name: colour.name,
          hex: colour.hex,
          savedAt: colour.savedAt,
          groupIds: remapped,
        };
      }
    });

    var deletedColours = Object.keys(deletedColoursMap).map(function (id) {
      return { productId: id, deletedAt: deletedColoursMap[id] };
    });

    return {
      schemaVersion: remoteState.schemaVersion || localState.schemaVersion,
      updatedAt: new Date().toISOString(),
      groups: groupMerge.groups,
      colours: Object.keys(coloursByProductId).map(function (id) { return coloursByProductId[id]; }),
      deletedGroupIds: deletedGroupIds,
      deletedColours: deletedColours,
    };
  }

  global.SavedColoursMerge = {
    mergeStates: mergeStates,
  };
})(window);
